import {issueLinkedPullRequestHovercardPath} from '@github-ui/paths'
import {
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
} from '@primer/octicons-react'
import {ActionList, Link} from '@primer/react'
import {useTheme} from '@primer/styled-react'
import {Octicon} from '@primer/styled-react/deprecated'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import type {PullRequest$key} from './__generated__/PullRequest.graphql'
import styles from './PullRequest.module.css'

type Props = {
  pullRequest: PullRequest$key
  hovercard?: boolean
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PullRequestFragment = graphql`
  fragment PullRequest on PullRequest {
    id
    title
    number
    merged
    closed
    isDraft
    repository {
      name
      owner {
        login
      }
    }
    url
  }
`

export function PullRequest({pullRequest, hovercard}: Props) {
  const {
    id,
    number,
    title,
    repository: {owner, name},
    url,
    merged,
    closed,
    isDraft,
  } = useFragment(PullRequestFragment, pullRequest)
  const {login} = owner

  const {theme} = useTheme()

  const getIcon = () => {
    if (merged) {
      return <Octicon icon={GitMergeIcon} size={16} sx={{color: theme?.colors.done.fg}} />
    } else if (closed) {
      return <Octicon icon={GitPullRequestClosedIcon} size={16} sx={{color: theme?.colors.closed.fg}} />
    } else if (isDraft) {
      return <Octicon icon={GitPullRequestDraftIcon} size={16} className={styles.draftIcon} />
    } else {
      return <Octicon icon={GitPullRequestIcon} size={16} sx={{color: theme?.colors.open.fg}} />
    }
  }

  if (hovercard) {
    return (
      <Link
        href={url}
        target="_blank"
        data-hovercard-url={issueLinkedPullRequestHovercardPath({
          owner: login,
          repo: name,
          pullRequestNumber: number,
        })}
        aria-label={`link to the pull request #${title}`}
        className={styles.hovercardLink}
      >
        <GitPullRequestIcon size={16} />
        <span className={styles.countText}>1</span>
      </Link>
    )
  }

  return (
    <ActionList.Item key={id}>
      <ActionList.LeadingVisual>{getIcon()}</ActionList.LeadingVisual>
      <a href={url} aria-label={`link to the issue #${title}`} className={styles.pullRequestItemContent}>
        <span>{title}</span>
        <span className={styles.repositoryInfo}>{`${name}#${number}`}</span>
      </a>
    </ActionList.Item>
  )
}
